/**
 * JUB-154: turns the wizard's answers into the PublicEvent the real templates
 * render, so the live preview (desktop) and the reward screen (W10) show the
 * couple's own data through the same renderEventTemplate() path as the
 * public page — never Lorem Ipsum.
 *
 * Kept separate from the editor's LandingTab preview on purpose: the wizard
 * only knows the answers, not the full landing settings.
 */
import { FALLBACK_TEMPLATES, resolveTemplateDefinition, type EventTemplateDefinition } from "@/lib/event-templates";
import { isTemplatePackage } from "@/lib/template-engine/resolve";
import type { EventCeremony } from "@/services/api/eventSettings";
import type { PublicEvent } from "@/services/api/publicEvents";
import { MOMENT_SLOTS, type MomentSlot, type OnboardingAnswers } from "./types";

export interface PreviewSource {
  uuid: string;
  url: string;
  theme: string | null;
  language: "ro" | "en";
  answers: OnboardingAnswers;
  /**
   * `GET /templates`, when it has answered: lets a chosen installed package
   * resolve to its own definition (JUB-213). Without it only the built-in
   * designs resolve.
   */
  catalog?: EventTemplateDefinition[] | null;
}

/**
 * The designs the preview can resolve a theme to: the built-in designs, always
 * from the bundled definitions, plus the installed packages of the catalog. An
 * unknown or retired key still lands on the default design.
 */
function previewRegistry(catalog: EventTemplateDefinition[] | null | undefined): EventTemplateDefinition[] {
  const packages = (catalog ?? []).filter(
    (template) => isTemplatePackage(template.config) && !FALLBACK_TEMPLATES.some((builtin) => builtin.key === template.key),
  );
  return packages.length > 0 ? [...FALLBACK_TEMPLATES, ...packages] : FALLBACK_TEMPLATES;
}

const FALLBACK_TITLES: Record<"ro" | "en", Record<MomentSlot, string>> = {
  ro: { civil: "Cununia civilă", religious: "Cununia religioasă", baptism: "Botezul", party: "Petrecerea" },
  en: { civil: "Civil ceremony", religious: "Religious ceremony", baptism: "The baptism", party: "The party" },
};

const clean = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/** "Ana & Andrei", "Sofia", "Ana & Andrei, Sofia" — what the hero shows. */
export function displayNames(answers: Pick<OnboardingAnswers, "type" | "bride" | "groom" | "children">): string {
  const couple = [clean(answers.bride), clean(answers.groom)].filter(Boolean).join(" & ");
  const children = answers.children.map(clean).filter(Boolean).join(" & ");
  if (answers.type === "baptism") return children;
  if (answers.type === "wedding") return couple;
  return [couple, children].filter(Boolean).join(", ");
}

export function buildPreviewEvent(source: PreviewSource): PublicEvent {
  const { answers } = source;
  const isBaptismOnly = answers.type === "baptism";
  const includesBaptism = answers.type !== "wedding";
  const children = answers.children.map(clean).filter((c): c is string => Boolean(c));
  const titles = FALLBACK_TITLES[source.language] ?? FALLBACK_TITLES.ro;

  const ceremonies: Record<MomentSlot, EventCeremony> = {} as Record<MomentSlot, EventCeremony>;
  for (const slot of MOMENT_SLOTS) {
    const moment = answers.moments[slot];
    const venue = clean(moment?.venue);
    // Same rule as the API: title, then venue, then the slot's name. A time
    // without a date stays hidden, exactly like on the public page.
    ceremonies[slot] = {
      enabled: Boolean(moment?.enabled),
      name: clean(moment?.title) ?? venue ?? titles[slot],
      title: moment?.title ?? null,
      venue: venue ?? null,
      address: clean(moment?.address) ?? null,
      city: clean(moment?.city) ?? null,
      datetime: answers.date && moment?.time ? `${answers.date}T${moment.time}:00` : null,
    };
  }

  const template = resolveTemplateDefinition(previewRegistry(source.catalog), source.theme);
  const parents = [clean(answers.parents?.mother), clean(answers.parents?.father)].filter(Boolean).join(" & ");

  return {
    uuid: source.uuid,
    name: displayNames(answers) || "",
    date: answers.date,
    type: isBaptismOnly ? 1 : 0,
    description: "",
    starBride: isBaptismOnly ? undefined : clean(answers.bride),
    starGroom: isBaptismOnly ? undefined : clean(answers.groom),
    starBaptism: includesBaptism ? children[0] : undefined,
    starBaptism2: includesBaptism ? children[1] : undefined,
    includesWedding: answers.type !== "baptism",
    includesBaptism,
    babyCount: includesBaptism ? Math.max(1, children.length) : null,
    currency: "RON",
    country: "ROU",
    url: source.url,
    theme: template.key,
    template,
    defaultLanguage: source.language,
    gallery: [],
    ceremonies,
    family: parents ? { parents, godparents: undefined } : undefined,
    rsvpCutoffDate: answers.rsvpDeadline,
    rsvpSettings: {
      rsvpEnabled: true,
      dueDate: answers.rsvpDeadline,
      kidsEnabled: false,
      accommodationEnabled: false,
      mealOptions: { standard: false, vegan: false, vegetarian: false },
    },
    showTimeline: true,
    showGallery: true,
    showStory: true,
  };
}
